import { api } from "@/services/api"

// Define channel types and responses
export type CreatableChannelType =
  | "whatsapp"
  | "whatsapp-business"
  | "instagram"
  | "facebook_messenger"
  | "facebook"
  | "telegram"
  | "website"
  | "website_chat"
export type ChannelType = CreatableChannelType | "preview"

export interface ChannelAgentStatus {
  is_enabled: boolean
  disabled_at: string | null
  disabled_until: string | null
  reason: string | null
  agent_is_active: boolean
}

export interface ChannelBillingIssue {
  code: number | null
  message: string
  details?: string | null
  resolve_url?: string | null
  detected_at?: string | null
  last_checked_at?: string | null
  source?: string | null
}

export interface ApiChannel {
  id: string
  name: string
  organization_id: string
  type: ChannelType
  status: "connected" | "disconnected" | "connecting"
  billing_issue?: ChannelBillingIssue | null
  status_details?: {
    qr_code?: string
    profile_picture_url?: string
    // Instagram
    username?: string
    // WhatsApp Business
    waba_name?: string
    phone_verified_name?: string
    business_name?: string
    // WhatsApp uazapi
    display_name?: string
    // Facebook Messenger
    page_name?: string
  }
  phone_number?: string
  agent_id?: string
  agent_status?: ChannelAgentStatus
  signature_enabled?: boolean
  signature_template?: string
  conversation_mode?: string
  comment_auto_reply_enabled?: boolean
  dm_auto_reply_enabled?: boolean
  accept_group_messages?: boolean
  transcribe_audio?: boolean
  process_images?: boolean
  process_files?: boolean
  is_deleted?: boolean
  deleted_at?: string
  created_at: string
  updated_at: string
}

export interface CreateChannelPayload {
  name: string
  type: CreatableChannelType
  agent_id?: string
  transcribe_audio?: boolean
  process_images?: boolean
  process_files?: boolean
}

interface WhatsappStatusResponse {
  channel_id: string
  status: string
  phone_number: string
}

interface WhatsappInitializeResponse {
  channel_id: string
  qr_code: string
  status: string
}

interface WhatsappConnectResponse {
  channel_id: string
  qr_code: string
  status: string
}

interface WhatsappDisconnectResponse {
  channel_id: string
  status: string
}

export interface WidgetConfig {
  primary_color: string
  position: "bottom-right" | "bottom-left"
  welcome_message: string
  placeholder_text: string
  avatar_url: string | null
  show_branding: boolean
  auto_open_delay: number | null
  bubble_icon: string
  offline_message: string
  collect_visitor_info: boolean
  allowed_domains: string[]
}

export interface WebsiteChatSetupResponse {
  channel_id: string
  widget_api_key: string
  embed_script: string
}

export interface WebsiteChatConfigResponse {
  channel_id: string
  widget_api_key: string
  widget_config: WidgetConfig
  embed_script: string
}

export interface ChannelStatusHistory {
  id: string
  channel_id: string
  old_status?: string
  new_status: string
  changed_at: string
  source: string
}

export interface ChatSyncStatus {
  status?: "idle" | "enqueued" | "in_progress" | "completed" | "failed"
  job_id?: string | null
  total?: number
  processed?: number
  created_conversations?: number
  created_contacts?: number
  skipped_lid?: number
  include_groups?: boolean
  started_at?: string | null
  completed_at?: string | null
  error?: string | null
}

export const channelService = {
  // Get all channels
  getChannels: async (includeDeleted = false): Promise<ApiChannel[]> => {
    const response = await api.get("/communication/channels", {
      params: includeDeleted ? { include_deleted: true } : undefined,
    })
    return Array.isArray(response.data) ? response.data : []
  },

  // Re-verify a WhatsApp Business channel's Meta billing state; the backend
  // clears the channel's billing_issue flag when Meta reports it resolved.
  checkWhatsappBusinessBilling: async (id: string): Promise<{ status: string }> => {
    const response = await api.post(`/communication/channels/${id}/whatsapp-business/billing-check`)
    return response.data
  },

  // Get a channel by ID
  getChannelById: async (id: string): Promise<ApiChannel | null> => {
    try {
      const response = await api.get(`/communication/channels/${id}`)
      return response.data
    } catch (error) {
      console.error(`Failed to fetch channel with id ${id}:`, error)
      return null
    }
  },

  // Create a new channel
  createChannel: async (payload: CreateChannelPayload): Promise<ApiChannel> => {
    const response = await api.post("/communication/channels", payload)
    return response.data
  },

  // Update an existing channel
  updateChannel: async (
    id: string,
    payload: Partial<CreateChannelPayload>
  ): Promise<ApiChannel> => {
    const response = await api.patch(`/communication/channels/${id}`, payload)
    return response.data
  },

  // Update channel settings (signature, conversation mode, etc.)
  updateChannelSettings: async (
    id: string,
    payload: {
      signature_enabled?: boolean
      signature_template?: string
      conversation_mode?: string
      comment_auto_reply_enabled?: boolean
      dm_auto_reply_enabled?: boolean
      accept_group_messages?: boolean
    }
  ): Promise<ApiChannel> => {
    const response = await api.patch(`/communication/channels/${id}/settings`, payload)
    return response.data
  },

  // Delete a channel
  deleteChannel: async (id: string): Promise<void> => {
    await api.delete(`/communication/channels/${id}`)
  },

  // Initialize WhatsApp for a channel
  initializeWhatsapp: async (channelId: string): Promise<WhatsappInitializeResponse> => {
    const response = await api.post<WhatsappInitializeResponse>(
      `/communication/channels/${channelId}/whatsapp/initialize`
    )
    return response.data
  },

  // Connect WhatsApp for a channel
  connectWhatsapp: async (channelId: string): Promise<WhatsappConnectResponse> => {
    const response = await api.post<WhatsappConnectResponse>(
      `/communication/channels/${channelId}/whatsapp/connect`
    )
    return response.data
  },

  // Disconnect WhatsApp for a channel
  disconnectWhatsapp: async (channelId: string): Promise<WhatsappDisconnectResponse> => {
    const response = await api.post<WhatsappDisconnectResponse>(
      `/communication/channels/${channelId}/whatsapp/disconnect`
    )
    return response.data
  },

  // Get WhatsApp status for a channel
  getWhatsappStatus: async (channelId: string): Promise<WhatsappStatusResponse> => {
    const response = await api.get<WhatsappStatusResponse>(
      `/communication/channels/${channelId}/whatsapp/status`
    )
    return response.data
  },

  // Get agent status for a channel
  getAgentStatus: async (channelId: string): Promise<ChannelAgentStatus> => {
    const response = await api.get<ChannelAgentStatus>(
      `/communication/channels/${channelId}/agent/status`
    )
    return response.data
  },

  // Disable agent for a channel
  disableAgent: async (
    channelId: string,
    untilDate?: Date,
    reason?: string
  ): Promise<ChannelAgentStatus> => {
    const response = await api.post<ChannelAgentStatus>(
      `/communication/channels/${channelId}/agent/disable`,
      {
        until_date: untilDate?.toISOString() ?? null,
        reason: reason ?? null,
      }
    )
    return response.data
  },

  // Enable agent for a channel
  enableAgent: async (channelId: string): Promise<ChannelAgentStatus> => {
    const response = await api.post<ChannelAgentStatus>(
      `/communication/channels/${channelId}/agent/enable`
    )
    return response.data
  },

  // Connect Facebook Messenger to a channel
  connectMessenger: async (
    channelId: string,
    payload: { page_id: string; access_token: string }
  ): Promise<{ status: string; page_id: string; page_name?: string }> => {
    const response = await api.post(
      `/communication/channels/${channelId}/connect-messenger`,
      payload
    )
    return response.data
  },

  // Website Chat methods
  setupWebsiteChat: async (channelId: string): Promise<WebsiteChatSetupResponse> => {
    const response = await api.post<WebsiteChatSetupResponse>(
      `/communication/channels/${channelId}/website-chat/setup`
    )
    return response.data
  },

  getWidgetConfig: async (channelId: string): Promise<WebsiteChatConfigResponse> => {
    const response = await api.get<WebsiteChatConfigResponse>(
      `/communication/channels/${channelId}/website-chat/config`
    )
    return response.data
  },

  updateWidgetConfig: async (
    channelId: string,
    config: Partial<WidgetConfig>
  ): Promise<WebsiteChatConfigResponse> => {
    const response = await api.put<WebsiteChatConfigResponse>(
      `/communication/channels/${channelId}/website-chat/config`,
      config
    )
    return response.data
  },

  regenerateWidgetApiKey: async (channelId: string): Promise<WebsiteChatSetupResponse> => {
    const response = await api.post<WebsiteChatSetupResponse>(
      `/communication/channels/${channelId}/website-chat/regenerate-key`
    )
    return response.data
  },

  getEmbedScript: async (channelId: string): Promise<{ embed_script: string }> => {
    const response = await api.get<{ embed_script: string }>(
      `/communication/channels/${channelId}/website-chat/embed-script`
    )
    return response.data
  },

  getStatusHistory: async (channelId: string, limit = 50): Promise<ChannelStatusHistory[]> => {
    const response = await api.get<ChannelStatusHistory[]>(
      `/communication/channels/${channelId}/status-history`,
      { params: { limit } }
    )
    return response.data
  },

  syncChats: async (
    channelId: string,
    includeGroups = true
  ): Promise<{ job_id?: string; status?: string }> => {
    const response = await api.post(`/communication/channels/${channelId}/sync-chats`, null, {
      params: { include_groups: includeGroups },
    })
    return response.data
  },

  getChatSyncStatus: async (channelId: string): Promise<ChatSyncStatus> => {
    const response = await api.get<ChatSyncStatus>(
      `/communication/channels/${channelId}/chat-sync-status`
    )
    return response.data
  },
}
